/**
 * =============================================================================
 * loadingLayout - Spec compartida de la pantalla de carga (logo + barra)
 * =============================================================================
 *
 * Fuente ÚNICA de verdad para el tamaño y la disposición de las pantallas de
 * loading, para que se vean EXACTAMENTE iguales en sus dos implementaciones:
 *  - DOM (login/transiciones): `AuthUI.showLoadingIndicator` + `loadingBarDom`.
 *  - Phaser (in-game): `GameScene.createLoadingScreen` + `LoadingBar`.
 *
 * Disposición: stack vertical centrado en pantalla → logo arriba, gap, barra
 * abajo. El DOM lo logra con flex; Phaser replica el mismo cálculo en px.
 *
 * Referencia visual: la pantalla de login (logo grande). Ver docs/UI_SKINNING.md.
 */

export const LOADING_LAYOUT = {
  /** Ancho máx del logo en px y fracción del viewport (lo menor de los dos). */
  logoMaxPx: 420,
  logoViewportFrac: 0.55,
  /** Ancho máx de la barra en px y fracción del viewport (lo menor de los dos). */
  barMaxPx: 600,
  barViewportFrac: 0.7,
  /** Separación vertical entre el logo y la barra (px). */
  gapPx: 24,
  /** Proporción ancho/alto del arte de la barra (frame 1933×119). */
  barAspect: 1933 / 119,
} as const;

/** Ancho del logo en px para un ancho de viewport/canvas dado (uso Phaser). */
export function loadingLogoWidthPx(viewportW: number): number {
  return Math.min(LOADING_LAYOUT.logoMaxPx, viewportW * LOADING_LAYOUT.logoViewportFrac);
}

/** Ancho de la barra en px para un ancho de viewport/canvas dado (uso Phaser). */
export function loadingBarWidthPx(viewportW: number): number {
  return Math.min(LOADING_LAYOUT.barMaxPx, viewportW * LOADING_LAYOUT.barViewportFrac);
}

/** Valor CSS responsivo del ancho del logo (uso DOM). Equivale a la versión px. */
export function loadingLogoWidthCss(): string {
  return `min(${LOADING_LAYOUT.logoMaxPx}px, ${LOADING_LAYOUT.logoViewportFrac * 100}vw)`;
}

/**
 * Glow negro EXTERNO del logo de AUREUM ONLINE — se usa en TODAS las pantallas
 * donde aparece el logo grande (loading, login, listado y creación de personaje)
 * para despegarlo del fondo. Dos drop-shadow sin offset (halo suave) apilados.
 * Fuente única para mantener el efecto consistente.
 */
export const LOGO_GLOW_FILTER =
  'drop-shadow(0 0 12px rgba(0, 0, 0, 0.9)) drop-shadow(0 0 28px rgba(0, 0, 0, 0.55))';

/** Valor CSS responsivo del ancho de la barra (uso DOM). Equivale a la versión px. */
export function loadingBarWidthCss(): string {
  return `min(${LOADING_LAYOUT.barMaxPx}px, ${LOADING_LAYOUT.barViewportFrac * 100}vw)`;
}
